import type { HelpGuide } from '../types';

export const payreqGuide: HelpGuide = {
  key: 'payreq',
  title: 'ขออนุมัติจ่ายเงิน (Payment Request)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ ขออนุมัติจ่ายเงิน',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/payreq/คู่มือการใช้งาน_ขออนุมัติจ่ายเงิน_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ขออนุมัติจ่ายเงิน (Payment Request)” ใช้ขออนุมัติและจ่ายเงินให้ผู้ขาย (Vendor) แบบครบวงจร — ตั้งแต่สร้างใบขอจ่าย ส่งให้ผู้มีอำนาจอนุมัติตามวงเงิน (maker-checker) จนถึงจ่ายเงินจริงและลงบัญชีให้อัตโนมัติ ระบบคำนวณ VAT และภาษีหัก ณ ที่จ่าย (WHT) ให้เอง พร้อมออกหนังสือรับรองหัก ณ ที่จ่าย (ภ.ง.ด. 53) เมื่อมีการจ่ายเงิน',
        },
        {
          kind: 'callout',
          text: 'วงจรเอกสาร 5 ขั้น: (1) สร้าง PR + แนบเอกสาร → (2) Approval Chain ตามวงเงิน → (3) JV1 ตั้งค่าใช้จ่ายค้างจ่าย (Dr ค่าใช้จ่าย / Cr ค่าใช้จ่ายค้างจ่าย) เมื่ออนุมัติครบ → (4) Cashier เลือกใบ → Batch Payment → (5) JV2 บันทึกการจ่าย (Dr ค่าใช้จ่ายค้างจ่าย / Cr เงินสด)',
        },
        { kind: 'para', text: 'หน้าจอมี 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ฉบับร่าง / รออนุมัติ — ',
              text: 'ใบที่ยังเป็นฉบับร่าง รออนุมัติ หรือถูกส่งกลับแก้ไข (แท็บเริ่มต้น)',
            },
            {
              lead: 'อนุมัติแล้ว · รอจ่าย — ',
              text: 'ใบที่อนุมัติครบแล้ว รอจ่ายเงิน (มีปุ่ม Batch Payment)',
            },
            { lead: 'จ่ายแล้ว — ', text: 'ใบที่จ่ายเงินแล้ว มี JV1 + JV2 ลงบัญชีครบ' },
            {
              lead: 'ตีกลับ / ยกเลิก — ',
              text: 'ใบที่ไม่อนุมัติ หรือถูกยกเลิก',
            },
            {
              lead: 'สรุปและรายงาน — ',
              text: 'สรุปยอดตามหมวดค่าใช้จ่าย พร้อมสถิติทั่วไป',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/payreq/01-register.png',
          caption: 'ทะเบียนใบขอจ่าย — KPI 4 ใบ, แถบวงจรเอกสาร 5 ขั้น และตารางฉบับร่าง / รออนุมัติ',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'รออนุมัติ — ',
              text: 'จำนวนใบที่รออนุมัติ พร้อมยอดเงินรวม (บาท)',
            },
            {
              lead: 'อนุมัติแล้ว · รอจ่าย — ',
              text: 'จำนวนใบที่อนุมัติแล้วรอจ่าย + ยอดรวม + จำนวนใบที่เลยกำหนดจ่าย',
            },
            {
              lead: 'จ่ายแล้วเดือนนี้ — ',
              text: 'ยอดเงินที่จ่ายแล้วในเดือนนี้ + จำนวนใบที่ JV1 + JV2 ลงครบ',
            },
            {
              lead: 'ตีกลับ / ยกเลิก — ',
              text: 'จำนวนใบที่ส่งกลับให้ผู้ขอแก้ไข หรือยกเลิก',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ใต้ KPI มีแถบ “วงจรเอกสาร 5 ขั้น” แสดงลำดับงาน ปุ่มมุมขวาบน: นำเข้า (อัปโหลดไฟล์ Excel สร้างหลายใบพร้อมกัน) · ส่งออก (ดาวน์โหลดรายการเป็น .xlsx) · สร้างใบขอจ่าย (ปุ่มสีแดง) ส่วนปุ่ม Batch Payment จะแสดงเฉพาะแท็บ “อนุมัติแล้ว · รอจ่าย” ตารางมีคอลัมน์: เลขที่ PR · Vendor · รายละเอียด · หมวด · กำหนดจ่าย · มูลค่ารวม · WHT · จ่ายสุทธิ · สถานะ · การกระทำ พร้อมแถวรวมยอดด้านล่าง — คลิกที่แถวเพื่อเปิดลิ้นชักรายละเอียด',
        },
      ],
    },
    {
      id: 'create',
      heading: '3. การสร้างใบขออนุมัติจ่ายเงิน',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม สร้างใบขอจ่าย แล้วกรอกข้อมูลด้านซ้าย: ผู้ขาย (Vendor) · วันที่ในบิล · สาขา · หน่วยงาน · หมวดค่าใช้จ่าย (GL Code) · รายละเอียด · จำนวนเงินก่อน VAT (Sub-total) · เลือกอัตรา VAT (7% หรือ 0% ส่งออก) · Payment Terms (Net 30/14/45/60/90) · วิธีชำระ (โอนเงินผ่านธนาคาร / เช็ค / เงินสด) · กำหนดจ่าย ระบบจะ generate เลขที่ PR-YYMM-NNNNNN ให้อัตโนมัติ',
        },
        {
          kind: 'para',
          text: 'ด้านขวาเป็น “พรีวิวการลงบัญชี (Auto)” ระบบคำนวณ VAT/WHT ให้แบบเรียลไทม์ และแสดงใบสำคัญที่จะลงให้: JV1 — ตั้งค่าใช้จ่ายค้างจ่าย (Dr หมวดค่าใช้จ่าย + ภาษีซื้อ 10190012 / Cr เจ้าหนี้การค้า 20150001 + WHT ค้างนำส่ง 20170011) และ JV2 — บันทึกการจ่ายเงิน (Dr เจ้าหนี้การค้า 20150001 / Cr เงินฝากธนาคาร (บัญชีหลักของบริษัท)) ถ้ามี WHT จะมีกล่องแจ้งว่าระบบจะออกหนังสือรับรองหัก ณ ที่จ่ายอัตโนมัติ และนำเข้า ภ.ง.ด. 53 ของเดือน',
        },
        {
          kind: 'para',
          text: 'เมื่อกรอกครบ (Vendor / หมวด / จำนวนเงิน / รายละเอียด) กดปุ่ม บันทึก (สร้างฉบับร่าง) ระบบจะสร้างใบเป็น “ฉบับร่าง” ก่อน แล้วค่อยส่งอนุมัติในขั้นถัดไป',
        },
        {
          kind: 'image',
          src: '/help/payreq/02-create.png',
          caption:
            'สร้างใบขออนุมัติจ่ายเงิน — ฟอร์มซ้าย + พรีวิวการลงบัญชี JV1 / JV2 อัตโนมัติด้านขวา',
        },
      ],
    },
    {
      id: 'detail',
      heading: '4. ลิ้นชักรายละเอียดและการส่งอนุมัติ',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกที่แถวใดก็ได้ในตารางเพื่อเปิดลิ้นชักรายละเอียดด้านขวา แสดง: ข้อมูลใบขอจ่าย (Vendor / หมวด / รายละเอียด / สาขา / กำหนดจ่าย) · จำนวนเงิน (Sub-total + VAT − WHT = จ่ายสุทธิ) · สายอนุมัติ (Approval Chain) แบบไทม์ไลน์ทีละขั้น · บันทึกการตรวจสอบ (Audit Log) ที่บันทึกทุกเหตุการณ์แบบเพิ่มได้อย่างเดียว และเมื่อจ่ายแล้วจะแสดงเลขที่ JV1 + JV2',
        },
        {
          kind: 'para',
          text: 'ใบฉบับร่างจะมีปุ่ม ส่งอนุมัติ (ใบที่ถูกตีกลับจะเป็น ส่งใหม่) เมื่อกดส่ง ระบบจะสร้างสายอนุมัติตามวงเงิน/สาขา/หน่วยงานโดยอัตโนมัติ และเปลี่ยนสถานะเป็น “รออนุมัติ ขั้น 1/N” ใบฉบับร่างหรือใบที่ถูกตีกลับสามารถกด ยกเลิก ได้',
        },
        {
          kind: 'image',
          src: '/help/payreq/03-detail-drawer.png',
          caption:
            'ลิ้นชักรายละเอียดใบรออนุมัติ — ยอดเงิน, สายอนุมัติ, บันทึกการตรวจสอบ และปุ่มพิจารณา',
        },
      ],
    },
    {
      id: 'approval',
      heading: '5. การพิจารณาอนุมัติ (Maker-Checker)',
      blocks: [
        {
          kind: 'para',
          text: 'ผู้มีอำนาจอนุมัติในขั้นปัจจุบันจะเห็นปุ่ม พิจารณา → ที่แถว หรือกด พิจารณา ในลิ้นชัก ระบบจะเปิดหน้าต่างพิจารณาให้เลือก 3 ทางเลือก: อนุมัติ (ผ่านไปขั้นถัดไป หรืออนุมัติครบ) · ส่งกลับแก้ไข (ส่งคืนผู้ขอพร้อมเหตุผล — แก้แล้วส่งใหม่ได้) · ไม่อนุมัติ (ปฏิเสธถาวรพร้อมเหตุผล)',
        },
        {
          kind: 'callout',
          text: 'แยกหน้าที่ (SoD): ผู้จัดทำ/ส่งอนุมัติใบเอง ไม่สามารถอนุมัติใบของตัวเองได้ · เมื่ออนุมัติครบขั้นสุดท้าย ระบบจะลงบัญชี JV1 (ตั้งค่าใช้จ่ายค้างจ่าย) ให้อัตโนมัติ',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/payreq/04-approval.png',
          caption: 'พิจารณาอนุมัติ — อนุมัติ / ส่งกลับแก้ไข / ไม่อนุมัติ พร้อมตัวนับขั้นอนุมัติ',
        },
      ],
    },
    {
      id: 'approved',
      heading: '6. แท็บอนุมัติแล้ว · รอจ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ อนุมัติแล้ว · รอจ่าย แสดงเฉพาะใบที่อนุมัติครบและรอจ่ายเงิน ใบที่เลยกำหนดจ่ายจะมีป้ายสีส้ม “เลยกำหนด” ในคอลัมน์กำหนดจ่าย แต่ละแถวมีช่องติ๊ก (checkbox) ให้เลือกใบที่ต้องการจ่าย เมื่อเลือกแล้วกดปุ่ม Batch Payment ที่มุมขวาบนเพื่อจ่ายรวบทีเดียว',
        },
        {
          kind: 'image',
          src: '/help/payreq/05-approved.png',
          caption: 'แท็บอนุมัติแล้ว · รอจ่าย — เลือกใบที่จะจ่ายด้วย checkbox แล้วกด Batch Payment',
        },
      ],
    },
    {
      id: 'batch',
      heading: '7. การจ่ายเงินแบบ Batch Payment (Cashier)',
      blocks: [
        {
          kind: 'para',
          text: 'หน้าต่าง Batch Payment · จ่ายเงินรวบทีเดียว แสดงสรุปยอด 4 ช่อง: จำนวนใบ PR · ยอดรวม (ก่อนหัก WHT) · หัก WHT รวม · โอนสุทธิ จากนั้นระบุ วันที่จ่าย (ค่าเริ่มต้น = วันนี้) และ COA บัญชีต้นทาง (เงินฝากธนาคาร บัญชีหลักของบริษัท) ด้านล่างเป็นตารางใบที่เลือกพร้อมยอดของแต่ละใบ',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม ยืนยัน · จ่ายเงิน {จำนวน} ใบ ระบบจะจ่ายทุกใบที่เลือก ลงบัญชี JV2 (Dr เจ้าหนี้การค้า 20150001 / Cr เงินฝากธนาคาร บัญชีหลักของบริษัท) ให้อัตโนมัติ และออกหนังสือรับรองหัก ณ ที่จ่ายสำหรับใบที่มี WHT (เข้า ภ.ง.ด. 53 ของเดือน) สถานะใบจะเปลี่ยนเป็น “จ่ายเงินแล้ว”',
        },
        {
          kind: 'image',
          src: '/help/payreq/06-batch-payment.png',
          caption: 'Batch Payment — สรุปยอด, วันที่จ่าย / บัญชีต้นทาง และตารางใบที่เลือก',
        },
      ],
    },
    {
      id: 'paid',
      heading: '8. แท็บจ่ายแล้ว',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ จ่ายแล้ว แสดงใบที่จ่ายเงินเรียบร้อยทั้งหมด สถานะ “จ่ายเงินแล้ว” (สีเขียว) ใบเหล่านี้มี JV1 + JV2 ลงบัญชีครบแล้ว — ใช้ตรวจสอบประวัติการจ่ายและกระทบยอดบัญชีค่าใช้จ่ายค้างจ่ายได้',
        },
        {
          kind: 'image',
          src: '/help/payreq/07-paid.png',
          caption: 'แท็บจ่ายแล้ว — ใบที่จ่ายเงินแล้ว พร้อมป้ายสถานะ “จ่ายเงินแล้ว”',
        },
      ],
    },
    {
      id: 'paid-detail',
      heading: '9. ลิ้นชักใบที่จ่ายแล้ว และการลงบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกใบในแท็บจ่ายแล้วเพื่อดูรายละเอียดครบถ้วน: สายอนุมัติที่ผ่านครบทุกขั้น (พร้อมชื่อผู้อนุมัติ/วันที่/ความเห็น) · บันทึกการจ่าย (ผู้จ่าย + วันที่จ่าย) · และกล่อง “การลงบัญชี” สีเขียวที่แสดงเลขที่ JV1 (ตั้งค่าใช้จ่ายค้างจ่าย) และ JV2 (จ่ายเงิน) เพื่อสอบกลับไปยังสมุดรายวันได้',
        },
        {
          kind: 'image',
          src: '/help/payreq/08-paid-detail.png',
          caption: 'ลิ้นชักใบที่จ่ายแล้ว — สายอนุมัติครบ, บันทึกการจ่าย และเลขที่ JV1 / JV2',
        },
      ],
    },
    {
      id: 'summary',
      heading: '10. แท็บสรุปและรายงาน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สรุปและรายงาน สรุปยอดใบขอจ่ายตามหมวดค่าใช้จ่าย: หมวด (รหัส + ชื่อ) · จำนวนใบ · มูลค่ารวม · สัดส่วน (แถบสัดส่วน) พร้อมแผง “สถิติทั่วไป” ด้านขวา ใช้ดูภาพรวมว่าค่าใช้จ่ายกระจุกตัวอยู่หมวดใดมากที่สุด',
        },
        {
          kind: 'image',
          src: '/help/payreq/09-summary.png',
          caption: 'แท็บสรุปและรายงาน — สรุปตามหมวดค่าใช้จ่าย พร้อมสถิติทั่วไป',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะของใบขอจ่าย',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['ฉบับร่าง', 'สร้างแล้วแต่ยังไม่ได้ส่งอนุมัติ (แก้ไข/ยกเลิกได้)'],
            ['รออนุมัติ ขั้น X/N', 'ส่งอนุมัติแล้ว กำลังรอผู้มีอำนาจในขั้นนั้นพิจารณา'],
            ['อนุมัติแล้ว · รอจ่าย', 'อนุมัติครบ JV1 ลงแล้ว รอ Cashier จ่ายเงิน'],
            ['จ่ายเงินแล้ว', 'จ่ายเงินแล้ว JV1 + JV2 ลงบัญชีครบ'],
            ['ส่งกลับแก้ไข', 'ผู้อนุมัติส่งคืนผู้ขอแก้ไข — แก้แล้วส่งใหม่ได้'],
            ['ไม่อนุมัติ', 'ปฏิเสธถาวร (ไม่สามารถส่งใหม่ได้)'],
            ['ยกเลิก', 'ยกเลิกใบฉบับร่าง / ใบที่ถูกตีกลับ (ไม่กระทบบัญชี)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมอนุมัติใบของตัวเองไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ระบบใช้หลักแยกหน้าที่ (SoD) — ผู้ที่จัดทำ/ส่งอนุมัติใบเอง จะอนุมัติใบของตัวเองไม่ได้ ต้องให้ผู้อื่นที่มีสิทธิเป็นผู้อนุมัติ',
            },
          ],
        },
        { kind: 'para', text: 'บัญชีลงเมื่อไหร่?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'JV1 (ตั้งค่าใช้จ่ายค้างจ่าย) ลงตอนอนุมัติครบขั้นสุดท้าย · JV2 (จ่ายเงิน) ลงตอนกด Batch Payment',
            },
          ],
        },
        { kind: 'para', text: '“ส่งกลับแก้ไข” ต่างจาก “ไม่อนุมัติ” อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ส่งกลับแก้ไข = ผู้ขอแก้ไขแล้วกด “ส่งใหม่” ได้ · ไม่อนุมัติ = ปฏิเสธถาวร ส่งใหม่ไม่ได้',
            },
          ],
        },
        { kind: 'para', text: 'หนังสือรับรองหัก ณ ที่จ่ายออกตอนไหน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ระบบออกอัตโนมัติเมื่อจ่ายเงิน (ใบที่มี WHT) และนำเข้า ภ.ง.ด. 53 ของเดือนนั้น',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'PR (Payment Request) — ',
              text: 'ใบขออนุมัติจ่ายเงิน เลขที่รูปแบบ PR-YYMM-NNNNNN',
            },
            {
              lead: 'Maker-Checker — ',
              text: 'การแยกผู้จัดทำกับผู้อนุมัติ เพื่อควบคุมภายใน (4-eyes)',
            },
            {
              lead: 'SoD (Segregation of Duties) — ',
              text: 'การแยกหน้าที่ — ผู้จัดทำอนุมัติเอกสารของตัวเองไม่ได้',
            },
            {
              lead: 'VAT — ',
              text: 'ภาษีมูลค่าเพิ่ม (7% หรือ 0% สำหรับการส่งออก)',
            },
            {
              lead: 'WHT — ',
              text: 'ภาษีหัก ณ ที่จ่าย หักจากผู้ขายและนำส่งสรรพากร (ภ.ง.ด. 53)',
            },
            {
              lead: 'JV1 — ',
              text: 'ใบสำคัญตั้งค่าใช้จ่ายค้างจ่าย (Dr ค่าใช้จ่าย / Cr ค่าใช้จ่ายค้างจ่าย) ลงตอนอนุมัติครบ',
            },
            {
              lead: 'JV2 — ',
              text: 'ใบสำคัญบันทึกการจ่ายเงิน (Dr 20150001 เจ้าหนี้การค้า / Cr เงินฝากธนาคาร บัญชีหลักของบริษัท) ลงตอนจ่ายเงิน',
            },
            {
              lead: 'Batch Payment — ',
              text: 'การจ่ายเงินหลายใบรวบทีเดียวโดย Cashier',
            },
            {
              lead: 'COA บัญชีต้นทาง — ',
              text: 'รหัสบัญชีเงินฝากธนาคารที่ใช้จ่ายเงิน (เงินฝากธนาคาร บัญชีหลักของบริษัท)',
            },
          ],
        },
      ],
    },
  ],
};
